import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';

// Lazy load all pages - each loads only when navigated to
const HomePage = lazy(() => import('./pages/HomePage'));
const B2COrderForm = lazy(() => import('./pages/B2COrderForm'));
const P2POrderForm = lazy(() => import('./pages/P2POrderForm'));
const ConfirmationPage = lazy(() => import('./pages/ConfirmationPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const RestaurantsPage = lazy(() => import('./pages/RestaurantsPage'));
const VanOrderForm = lazy(() => import('./pages/VanOrderForm'));
const RestaurantDashboard = lazy(() => import('./pages/RestaurantDashboard'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TrackingPage = lazy(() => import('./pages/TrackingPage'));
const SlidesPage = lazy(() => import('./pages/SlidesPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));

const DriverDashboard = lazy(() => import('./pages/DriverDashboard'));
const ProspecteurDashboard = lazy(() => import('./pages/ProspecteurDashboard'));
const RestaurantPartnerPage = lazy(() => import('./pages/RestaurantPartnerPage'));
const DriverGuidePage = lazy(() => import('./pages/DriverGuidePage'));
const DriverShareCard = lazy(() => import('./pages/DriverShareCard'));
const DriverPromoInfo = lazy(() => import('./pages/DriverPromoInfo'));
const GetApp = lazy(() => import('./pages/GetApp'));
const ShopPublicPage = lazy(() => import('./pages/ShopPublicPage'));
const BornePage = lazy(() => import('./pages/BornePage'));
const TicketBornePage = lazy(() => import('./pages/TicketBornePage'));
const ContractSignPage = lazy(() => import('./pages/ContractSignPage'));
const MeetPage = lazy(() => import('./pages/MeetPage'));
const FlexyPage = lazy(() => import('./pages/FlexyPage'));

// Loading spinner shown while a page chunk loads
const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
      <p style={{ color: '#64748b', fontSize: 14 }}>Loading...</p>
    </div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#dc2626' }}>Something went wrong</h2>
          <p style={{ color: '#666', margin: '16px 0' }}>{this.state.error?.message}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{ padding: '10px 24px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/b2c" element={<B2COrderForm />} />
              <Route path="/p2p" element={<P2POrderForm />} />
              <Route path="/restaurants" element={<RestaurantsPage />} />
              <Route path="/van" element={<VanOrderForm />} />
              <Route path="/restaurant-dashboard" element={<RestaurantDashboard />} />
              <Route path="/partner/:slug" element={<PartnerDashboard />} />
              <Route path="/driver" element={<DriverGuidePage />} />
              <Route path="/delivery" element={<DriverDashboard />} />
              <Route path="/prospecteur" element={<ProspecteurDashboard />} />
              <Route path="/restaurant-partner" element={<RestaurantPartnerPage />} />
              <Route path="/confirmation/:orderId" element={<ConfirmationPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-cancel" element={<PaymentCancelPage />} />
              <Route path="/admin" element={<AdminDashboard country="fi" />} />
              <Route path="/admin/fr" element={<AdminDashboard country="fr" />} />
              <Route path="/admin/dz" element={<AdminDashboard country="dz" />} />
              <Route path="/admin/ma" element={<AdminDashboard country="ma" />} />
              <Route path="/admin/tn" element={<AdminDashboard country="tn" />} />
              <Route path="/admin/se" element={<AdminDashboard country="se" />} />
              <Route path="/admin/no" element={<AdminDashboard country="no" />} />
              <Route path="/admin/us" element={<AdminDashboard country="us" />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/track/:trackingId" element={<TrackingPage />} />
              <Route path="/slides" element={<SlidesPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/id" element={<DriverPromoInfo />} />
              <Route path="/id/:publicId" element={<DriverShareCard />} />
              <Route path="/get-app" element={<GetApp />} />
              <Route path="/shop/:slug" element={<ShopPublicPage />} />
              {/* Borne de commande posee dans la boutique, ouverte en plein ecran sur une tablette. */}
              <Route path="/borne/:slug" element={<BornePage />} />
              {/* Ticket d'une commande de borne : le client y arrive en scannant le QR code de la borne. */}
              <Route path="/ticket/:id" element={<TicketBornePage />} />
              <Route path="/contracts/sign/:token" element={<ContractSignPage />} />
              <Route path="/meet/:meetingId" element={<MeetPage />} />
              {/* Recharges telephoniques Algerie payees en euros (page publique + suivi ?commande=...). */}
              <Route path="/flexy" element={<FlexyPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
